import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import { exploreClasses, classCategories } from "../../mocks/explore";
import { useGrowStore } from "../../hooks/useGrowStore";

const levelColor: Record<string, string> = {
  "입문": "bg-[#E8F5E9] text-[#2E7D32]",
  "중급": "bg-[#FFF3E0] text-[#E65100]",
  "고급": "bg-[#FCE4EC] text-[#880E4F]",
};

export default function ExplorePage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { isClassCompleted } = useGrowStore();

  const filtered = exploreClasses.filter((c) => {
    const matchCat = activeCategory === "all" || c.category === activeCategory;
    const matchSearch =
      searchQuery === "" ||
      c.title.includes(searchQuery) ||
      c.desc.includes(searchQuery);
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Header */}
      <section className="pt-24 pb-12 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="mb-3">
            <span className="text-[#00D873] text-sm font-bold tracking-widest uppercase">
              AI Classes
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-[#191F28] leading-tight tracking-tight mb-5">
            어떤 AI 능력을<br />키워볼까요?
          </h1>
          <p className="text-lg text-[#8B95A1] max-w-xl leading-relaxed mb-10">
            하루 15분으로 시작하는 실전 AI 클래스. 지루한 강의 없이<br className="hidden md:block" />
            챌린지 방식으로 빠르게 실력을 키워보세요.
          </p>

          {/* Search Bar */}
          <div className="relative max-w-lg">
            <div className="w-5 h-5 flex items-center justify-center absolute left-4 top-1/2 -translate-y-1/2">
              <i className="ri-search-line text-[#8B95A1] text-lg" />
            </div>
            <input
              ref={searchRef}
              type="text"
              placeholder="클래스 검색하기..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-[#F2F4F6] rounded-2xl text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30 transition-all"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-6 mb-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex gap-2.5 flex-wrap">
            {classCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.id
                    ? "bg-[#191F28] text-white"
                    : "bg-[#F2F4F6] text-[#8B95A1] hover:bg-[#E8E9EB] hover:text-[#191F28]"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Classes Grid */}
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-5xl text-[#E0E2E6]" />
              </div>
              <p className="text-[#8B95A1] text-lg font-medium">검색 결과가 없어요</p>
              <p className="text-[#8B95A1] text-sm mt-1">다른 키워드로 찾아보세요</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((cls) => {
                const completed = isClassCompleted(cls.id);
                return (
                  <div
                    key={cls.id}
                    onMouseEnter={() => setHoveredId(cls.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={`group bg-white rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1.5 ${
                      completed
                        ? "border-[#00D873]/40"
                        : "border-[#F2F4F6] hover:border-[#00D873]/40"
                    }`}
                    style={{
                      boxShadow: hoveredId === cls.id
                        ? "0 8px 32px rgba(0,216,115,0.12)"
                        : "0 2px 12px rgba(25,31,40,0.04)",
                    }}
                  >
                    {/* Card Image */}
                    <div className="relative h-[155px] overflow-hidden">
                      <img
                        src={cls.image}
                        alt={cls.title}
                        className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                      {/* Tag */}
                      <span className="absolute top-3 left-3 text-xs font-bold bg-white/90 backdrop-blur-sm text-[#191F28] px-2.5 py-1 rounded-full">
                        {cls.tag}
                      </span>
                      {/* Completed Badge */}
                      {completed && (
                        <span className="absolute top-3 right-3 bg-[#00D873] text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                          <i className="ri-check-line text-xs" />
                          완료
                        </span>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: cls.iconBg }}
                        >
                          <i className={`${cls.icon} text-lg`} style={{ color: cls.iconColor }} />
                        </div>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${levelColor[cls.level]}`}>
                          {cls.level}
                        </span>
                      </div>

                      <h3 className="text-[15px] font-bold text-[#191F28] leading-snug mb-1.5 line-clamp-2">
                        {cls.title}
                      </h3>
                      <p className="text-xs text-[#8B95A1] leading-relaxed mb-3 line-clamp-2">
                        {cls.desc}
                      </p>

                      <div className="flex items-center gap-3 mb-4">
                        <span className="flex items-center gap-1 text-xs text-[#8B95A1]">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-time-line" />
                          </span>
                          {cls.duration}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-[#8B95A1]">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-book-open-line" />
                          </span>
                          {cls.lessons}개 과제
                        </span>
                      </div>

                      {/* Social Badge + CTA Button */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-[#8B95A1] font-medium whitespace-nowrap">
                          🔥 {cls.participants.toLocaleString()}명
                        </span>
                        <button
                          onClick={() => navigate(`/learn/${cls.id}`)}
                          className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                            completed
                              ? "bg-[#F2F4F6] text-[#8B95A1] hover:bg-[#E8E9EB]"
                              : "bg-[#00D873] text-white hover:opacity-90 hover:-translate-y-0.5"
                          }`}
                        >
                          {completed ? (
                            <>
                              <i className="ri-refresh-line text-xs" />
                              다시 하기
                            </>
                          ) : (
                            <>
                              지금 시작하기
                              <i className="ri-arrow-right-line text-xs" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto">
          <div className="bg-[#F2F4F6] rounded-3xl p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-xs font-bold text-[#00D873] tracking-widest uppercase mb-2">기관 &amp; 팀 도입</p>
              <h2 className="text-2xl font-extrabold text-[#191F28] leading-tight">
                학교/기업 맞춤형 AI 교육<br />도입을 원하신다면?
              </h2>
              <p className="text-[#8B95A1] text-sm mt-2">48개 기관 도입, 전담 강사 배정, 맞춤형 커리큘럼</p>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              className="bg-[#191F28] text-white font-bold px-8 py-4 rounded-2xl whitespace-nowrap cursor-pointer hover:bg-[#2D3748] transition-colors text-sm flex-shrink-0"
            >
              요금제 보기 →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
